import CardCommon from '@op/components/CardCommon';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import {
  tableIcon,
  lineIcon,
  barIcon,
  pieIcon,
  stackLineIcon,
  SymbolType,
} from '@op/constants/chart';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useIntl } from 'umi';
import { gameInfoType } from '@op/types/common';
import { downloadAction } from '@op/pages/download/utils';
import './index.scss';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import BubbleCommon, {
  keyMetricsByMentionsTypeConsoleTips,
  keyMetricsByMentionsTypeMobileTips,
  keyMetricsByMentionsTypePCTips,
} from '@op/components/BubbleCommon';
import {
  ByTypeSelectType,
  sentimentMentionsOneLevelOptionsMap,
} from '@op/pages/Overview/KeyMetrics/constants';
import {
  getRenderList,
  SentimentMentionsTwoLevelType,
} from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import { useKeyMetricsMentionsChart } from '@op/pages/Overview/KeyMetrics/hooks';
import Charts from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/index';
import { reviewsFormat } from '@op/utils/value';
import { AnalysisSummaryTypeTab } from '../../types';

const heightMap: Record<string, number> = {
  pie: 304,
  table: 238,
  loading: 303,
  empty: 303,
  bodyHeight: 379,
};

export default function MentionsBrandHealthChart({
  tab,
  filter,
  gameInfo,
}: {
  tab: AnalysisSummaryTypeTab;
  filter: any;
  gameInfo: gameInfoType;
}) {
  const { formatMessage } = useIntl();
  const [mentions, setMentions] =
    useState<ByTypeSelectType>('by_mentions_type');

  const [oneOptions, setOneOptions] = useState<
    Array<{ text: string | React.ReactNode; value: string }>
  >([]);
  const [type, setType] = useState<SymbolType>('bar');

  const byMentionsTipsMap = useMemo(() => {
    if (gameInfo.entity_type === 'mobile') {
      return keyMetricsByMentionsTypeMobileTips;
    }
    if (gameInfo.entity_type === 'pc') {
      return keyMetricsByMentionsTypePCTips;
    }
    if (gameInfo.entity_type === 'console') {
      return keyMetricsByMentionsTypeConsoleTips;
    }

    return undefined;
  }, [gameInfo.entity_type]);

  const { leftTitle, rightTitle, tooltipConfig } = useMemo(() => {
    let leftTitle = formatMessage({ id: 'Mentions' });
    let rightTitle = formatMessage({ id: 'Brand Health' });
    let tooltipConfig = undefined;
    if (tab === 'google play' || tab === 'app store') {
      leftTitle = formatMessage({ id: 'Filtered Reviews' });
      rightTitle = formatMessage({ id: 'Rating' });
      tooltipConfig = {
        total: {
          title: 'Sum of Rating',
          valueFormat: (value: number) => `${reviewsFormat(value)}(100%)`,
        },
      };
    }

    if (tab === 'steam') {
      leftTitle = formatMessage({ id: 'Steam Filtered Reviews' });
      rightTitle = formatMessage({ id: 'Positive%' });
      tooltipConfig = {
        total: {
          title: formatMessage({ id: 'Filtered Reviews' }),
          valueFormat: (value: number) => `${reviewsFormat(value)}(100%)`,
        },
        showSpecialItemLast: true,
      };
    }

    if (type !== 'bar') rightTitle = '';

    return {
      leftTitle,
      rightTitle,
      tooltipConfig,
    };
  }, [formatMessage, tab, type]);

  const extParams = useMemo(() => {
    const realMentions =
      mentions === 'by_rating_ratio' ? 'by_rating' : mentions;
    let tabType = '';
    let metrics:
      | SentimentMentionsTwoLevelType
      | SentimentMentionsTwoLevelType[] = ['mentions_brand_health'];
    let extByType = 'brand_health';

    if (tab === 'google play' || tab === 'app store') {
      tabType = 'game_store';
      metrics = ['mobile_selected_avg_score', 'mobile_lifetime_avg_score'];
    } else if (tab === 'community') {
      tabType = 'social_media';
    } else if (tab === 'steam') {
      tabType = 'game_store';
      metrics = [
        'steam_review_positive',
        'steam_filtered_positive',
        'lifetime_positive',
        'lifetime_negative',
      ];
    }

    return {
      tab_type: tabType || tab,
      by_type: realMentions,
      metrics,
      ext_by_type: extByType,
    };
  }, [mentions, tab]);

  useEffect(() => {
    const options = sentimentMentionsOneLevelOptionsMap[tab]
      .filter(({ pcFilter }) =>
        gameInfo.entity_type === 'mobile' ? true : !pcFilter,
      )
      .map((val) => {
        const { value, text } = val;
        if (value === 'by_mentions_type') {
          return {
            value,
            text: (
              <BubbleCommon
                tipsMap={byMentionsTipsMap}
                placement={'right'}
                childStyle={{ width: '100%', paddingRight: '12px' }} // 让气泡展示在选项外
              >
                {formatMessage({ id: text })}
              </BubbleCommon>
            ),
          };
        }
        return {
          value,
          text: formatMessage({ id: text }),
        };
      });

    setOneOptions(options);
    if (!options.find((val) => val.value === mentions)) {
      setMentions(options[0].value);
    }
    // setMentions(options[0].value);
    // 接口请求全靠metrics字段来驱动，所以这里重复赋值，保证tab切换和下拉切换的时候能生效
  }, [byMentionsTipsMap, formatMessage, gameInfo, tab]);

  const hideMetricsSelect = ['pie', 'line', 'stack_line'].includes(type);

  const params = useMemo(() => {
    const obj = {
      ...filter,
      games: [],
      ...extParams,
    };

    return obj;
  }, [extParams, filter]);

  const renderKeysList = getRenderList({
    currentTab: tab,
    gameInfo,
    by_type: mentions,
    key_metrics: extParams.metrics,
    need_all_list: true,
    filter
  });

  const isRatingRatio = mentions === 'by_rating_ratio';

  const { data, loading } = useKeyMetricsMentionsChart({
    filter: params,
    extraData: renderKeysList,
    isRatingRatio,
    needEventData: false,
  });

  const operations = useMemo(() => {
    const list = [tableIcon, lineIcon, stackLineIcon, barIcon];

    if (!isRatingRatio) {
      list.unshift(pieIcon);
    }

    return list;
  }, [isRatingRatio]);

  function changeChartType(type: SymbolType) {
    setType(type || 'bar');
  }

  return (
    <CardCommon
      className="dbo-card-common dbo-card-common--by-sentiment"
      hasDownload
      operationInfo={[type, changeChartType]}
      operations={operations}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'KeyMetricsMentionsSentiment',
          fileName: 'Keyword & URL Drill',
          filter,
          auxiliaryParams: {
            currentTab: tab,
            gameInfo,
            by_type: mentions,
            metrics: extParams.metrics,
            isRatingRatio,
          },
          data: data.tableData,
        });
      }}
      title={
        tab === 'total' || tab === 'community' ? (
          <div className="dbo-card-selects">
            <SortBySelect
              defaultValue={mentions}
              onChange={(key: string, value: any) => {
                buriedBtnPoint('KMMS_ByMetricsType', value);
                setMentions(value);
              }}
              defaultParams={{
                options: oneOptions,
                needFormatMessage: false,
                btnTextPrefix: formatMessage({ id: 'Mentions' }),
                matchButtonWidth: true,
                dealwithButton: (value: any, { textMap }: any) =>
                  textMap[value],
              }}
            />
          </div>
        ) : undefined
      }
      loading={loading}
      loadingHeight={heightMap.loading}
      bodyStyle={{ height: heightMap.bodyHeight }}
      empty={data.empty}
      emptyHeight={heightMap.empty}
      logBtnId="ChartCard"
      extraInfo={'Mentions'}
    >
      <Charts
        tab={tab}
        gameInfo={gameInfo}
        by_type={mentions}
        type={type}
        filter={filter}
        chartEventSetting={{
          showEventAxis: false,
          showEventBubbles: true,
        }}
        // eventData={eventData}
        metrics={extParams.metrics}
        rightTitle={rightTitle}
        isRatingRatio={isRatingRatio}
        hideMetricsSelect={hideMetricsSelect}
        data={data}
        leftTitle={leftTitle}
        tooltipConfig={tooltipConfig}
        height={heightMap[type] ?? 274}
      />
    </CardCommon>
  );
}
